import { type ApolloClient } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { logError } from 'fergy-core-react-logging';
import { useContext } from 'react';
import type {
	ProjectsQueryVariables,
	ConsumerLandingPageQuery,
	LikeQuickStartMutation,
	LikeQuickStartMutationVariables,
	ProjectsQuery,
	UnlikeQuickStartMutation,
	UnlikeQuickStartMutationVariables
} from '../../../client/__generated__/graphql-client-types';
import { QuickStartContext } from '../../contexts/quickstart/quickstart.context';
import { doIfDefined } from '../../helpers/general-helper/general-helper';
import { LIKE_QUICKSTART, UNLIKE_QUICKSTART } from '../../queries/project/project-quickstart.queries';

export const useReactToQuickStart = () => {
	const [likeQuickStart] = useMutation<LikeQuickStartMutation, LikeQuickStartMutationVariables>(LIKE_QUICKSTART, {
		refetchQueries: ['ShopByLookPage']
	});
	const [unlikeQuickStart] = useMutation<UnlikeQuickStartMutation, UnlikeQuickStartMutationVariables>(UNLIKE_QUICKSTART, {
		refetchQueries: ['ShopByLookPage']
	});
	const { favoritedQuickStarts, setFavoritedQuickStarts } = useContext(QuickStartContext);
	function reactToQuickStart(quickStartId: number) {
		if (favoritedQuickStarts.includes(quickStartId)) {
			// Need to delete the like reaction
			unlikeQuickStart({
				variables: {
					projectId: quickStartId
				}
			})
				.then(() => {
					setFavoritedQuickStarts(favoritedQuickStarts.filter((favoritedQuickStartId) => favoritedQuickStartId !== quickStartId));
				})
				.catch((error) => {
					logError(error);
				});
		} else {
			// Need to like the quick start
			likeQuickStart({
				variables: {
					projectId: quickStartId
				}
			})
				.then(() => {
					setFavoritedQuickStarts([...favoritedQuickStarts, quickStartId]);
				})
				.catch((error) => {
					logError(error);
				});
		}
	}
	return reactToQuickStart;
};

export const loadMoreProjects = (
	setPage: (value: React.SetStateAction<number>) => void,
	fetchMore: ({ variables }: { variables: ProjectsQueryVariables }) => void,
	debouncedSearchTerm: string,
	ignorePermissions: boolean = false
) => {
	return setPage((prevPage) => {
		const newPage = prevPage + 1;
		fetchMore({
			variables: {
				input: {
					term: debouncedSearchTerm,
					ignorePermissions,
					paging: {
						page: newPage,
						useInfiniteScroll: true
					}
				}
			}
		});
		return newPage;
	});
};

export const debounceSearchTerm = async (
	projectListRef: React.RefObject<HTMLDivElement>,
	loading: boolean,
	refetch: ({ input }: ProjectsQueryVariables) => Promise<ApolloClient.QueryResult<ProjectsQuery | ConsumerLandingPageQuery>>,
	debouncedSearchTerm: string,
	setPage: (value: React.SetStateAction<number>) => void,
	setIsUpdatingList?: (value: React.SetStateAction<boolean>) => void,
	onUpdatedProjects?: (value: ProjectsQuery) => void
) => {
	if (!loading) {
		setPage(1);
		setIsUpdatingList?.(true);
		doIfDefined(projectListRef.current, (projectList) => projectList.scrollIntoView());
		const { data: updatedProjects } = await refetch({
			input: {
				term: debouncedSearchTerm,
				paging: {
					page: 1,
					useInfiniteScroll: true
				}
			}
		});

		if (updatedProjects && updatedProjects.projects) {
			onUpdatedProjects?.(updatedProjects);
		}

		return updatedProjects;
	}
	return undefined;
};
